import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { Role } from "./useRoles";

export const USERS_PAGE_SIZE = 50;
export const USERS_FILTER_DEBOUNCE_MS = 400;

const ACTIVITY_STATUS_DELETED = "DELETED";

const USER_SORT = {
  LastLoginAsc: "LAST_LOGIN_ASC",
  LastLoginDesc: "LAST_LOGIN_DESC",
  FirstNameAsc: "FIRST_NAME_ASC",
  FirstNameDesc: "FIRST_NAME_DESC",
  LastNameAsc: "LAST_NAME_ASC",
  LastNameDesc: "LAST_NAME_DESC",
} as const;
type UserSort = (typeof USER_SORT)[keyof typeof USER_SORT];

const DEFAULT_SORT: UserSort[] = [USER_SORT.LastLoginDesc];

// The UI's "login" column shows newest activity first on its default (asc)
// state, which is the inverse of ascending last_login — preserve that.
const SORT_BY_COLUMN: Record<string, { asc: UserSort[]; desc: UserSort[] }> = {
  login: {
    asc: [USER_SORT.LastLoginDesc],
    desc: [USER_SORT.LastLoginAsc],
  },
  name: {
    asc: [USER_SORT.FirstNameAsc, USER_SORT.LastNameAsc],
    desc: [USER_SORT.FirstNameDesc, USER_SORT.LastNameDesc],
  },
};

export interface UsersPageFilter {
  name_ilike?: string;
  email_ilike?: string;
  role_name_in?: string[];
  not: { activity_status: typeof ACTIVITY_STATUS_DELETED };
}

export interface GetUsersPageVariables {
  first: number;
  after: string;
  sort: UserSort[];
  filters: UsersPageFilter;
}

interface BuildUsersPageVariablesOptions {
  pageSize: number;
  pageIndex: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  roles: Role[];
}

// The backend applies the value as a raw SQL ILIKE pattern with `\` as the
// escape character, so wildcards typed by the user must match literally.
export const toIlikePattern = (term: string): string =>
  `%${term.trim().replace(/[\\%_]/g, "\\$&")}%`;

const buildSort = (sorting: SortingState): UserSort[] => {
  const [activeSort] = sorting;
  const byColumn = activeSort && SORT_BY_COLUMN[activeSort.id];
  if (!byColumn) {
    return DEFAULT_SORT;
  }
  return activeSort.desc ? byColumn.desc : byColumn.asc;
};

const getFilterValue = (
  columnFilters: ColumnFiltersState,
  columnId: string
): unknown => columnFilters.find((filter) => filter.id === columnId)?.value;

const buildFilters = (
  columnFilters: ColumnFiltersState,
  roles: Role[]
): UsersPageFilter => {
  const filters: UsersPageFilter = {
    not: { activity_status: ACTIVITY_STATUS_DELETED },
  };

  const name = getFilterValue(columnFilters, "name");
  if (typeof name === "string" && name.trim() !== "") {
    filters.name_ilike = toIlikePattern(name);
  }

  const email = getFilterValue(columnFilters, "email");
  if (typeof email === "string" && email.trim() !== "") {
    filters.email_ilike = toIlikePattern(email);
  }

  const roleLabels = getFilterValue(columnFilters, "role");
  if (Array.isArray(roleLabels) && !roleLabels.includes("")) {
    const roleNames = roles
      .filter((role) => roleLabels.includes(role.label))
      .map((role) => role.label);
    if (roleNames.length) {
      filters.role_name_in = roleNames;
    }
  }

  return filters;
};

// Whether any column filter carries a value. The role filter stays in the list as `[""]` ("All")
// once it has been opened, so counting entries would call an empty tenant "No results".
export const hasActiveUserFilters = (
  columnFilters: ColumnFiltersState
): boolean =>
  columnFilters.some(({ value }) =>
    Array.isArray(value)
      ? value.some((entry) => entry !== "")
      : typeof value === "string"
        ? value.trim() !== ""
        : value !== null && value !== undefined
  );

export const buildUsersPageVariables = ({
  pageSize,
  pageIndex,
  sorting,
  columnFilters,
  roles,
}: BuildUsersPageVariablesOptions): GetUsersPageVariables => ({
  first: pageSize,
  after: btoa(`arrayconnection:${(pageIndex - 1) * pageSize - 1}`),
  sort: buildSort(sorting),
  filters: buildFilters(columnFilters, roles),
});
